import { Outlet, useNavigate, Link } from 'react-router-dom'
import { useAuth } from '@navix/auth-ui'
import { useAuthStore } from '../../stores/authStore'
import { usePermissionStore } from '../../stores/permissionStore'
import { useCanAccess } from '../../hooks/useCanAccess'
import SidePanel from './SidePanel'
import ClientSelector from '../client/ClientSelector'
import SiteSelector from '../site/SiteSelector'
import '../../styles/layout.css'

type RoleProfile = {
  label: string
  className: string
}

function useRoleProfile(): RoleProfile | null {
  const isSuperadmin = usePermissionStore((s) => s.isSuperadmin)
  const resources = usePermissionStore((s) => s.resources)
  const loaded = usePermissionStore((s) => s.loaded)

  if (!loaded) return null

  if (isSuperadmin) {
    return { label: 'Superadmin', className: 'role-badge--superadmin' }
  }

  // Check if user has write access to key admin resources
  const hasAdminResources = resources['clients'] || resources['sites'] || resources['users']
  if (hasAdminResources) {
    return { label: 'Admin', className: 'role-badge--admin' }
  }

  return { label: 'Viewer', className: 'role-badge--viewer' }
}

export default function MainLayout() {
  const { logout: authLogout } = useAuth()
  const storeLogout = useAuthStore((s) => s.logout)
  const permEmail = usePermissionStore((s) => s.email)
  const navigate = useNavigate()
  const canAdmin = useCanAccess('clients', 'read')
  const roleProfile = useRoleProfile()

  const handleLogout = () => {
    authLogout()
    storeLogout() // token → null triggers resetAllStores() via SessionManager
    navigate('/login', { replace: true })
  }

  return (
    <div className="app-layout">
      <header className="app-header">
        <span className="app-header__title">GeoContexte</span>
        <ClientSelector />
        <SiteSelector />
        <div className="app-header__actions">
          {permEmail && (
            <span className="app-header__email">
              {permEmail}
              {roleProfile && (
                <span className={`role-badge ${roleProfile.className}`}>
                  {roleProfile.label}
                </span>
              )}
            </span>
          )}
          {canAdmin && <Link to="/admin" className="app-header__admin">Admin</Link>}
          <button className="app-header__logout" onClick={handleLogout}>
            Deconnexion
          </button>
        </div>
      </header>
      <div className="app-content">
        <SidePanel />
        <main className="viewer-area">
          <Outlet />
        </main>
      </div>
    </div>
  )
}
